import { useEffect, useState } from "react";
import { useParams } from "react-router";
import queryKeys from "../configs/react-query/query-key";
import { normalizeResponseData, getAppLanguage } from "../utils/common";
import { useMutationData } from "./useMutationData";
import { extractErrorCode, formatSbomApiError } from "@utils/error";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useTranslation } from "react-i18next";
import { openErrorDetailsModal } from "@apis/errorModalApi";
import { SBOM_VERIFICATION_TYPE_CLARITY_SC } from "@constants/sbom";

const SUCCESS_CODE = "00000";

export const useSbomVerificationRequest = (currentPage) => {
  const { t } = useTranslation();
  const [listData, setListData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [verificationData, setVerificationData] = useState([]);
  const { projectSeq } = useParams();
  const language = getAppLanguage();

  const checkDuplicateVerification = useMutationData(
    queryKeys.checkDuplicateVerificationUpload,
    {
      method: "POST",
      params: { projectId: projectSeq, verificationType: "sbom" },
    },
    {
      onError: (error) => {
        console.error("Error during checksum verification:", error);
      },
    },
  );

  const detectSbomFormatMutation = useMutationData(
    queryKeys.detectSbomFormat,
    {
      method: "POST",
      skipErrorToast: true,
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onError: (error) => {
        console.error("Error detecting SBOM format:", error);
      },
    },
  );

  const detectSbomFormat = async (file) => {
    const formData = new FormData();
    formData.append("sbomFile", file);
    const response = await detectSbomFormatMutation.mutateAsync(formData);
    if (response?.code === SUCCESS_CODE && response?.data) {
      return response.data;
    }
    return {
      status: "UNRECOGNIZED",
      sbomType: null,
      requiresSbomSeq: false,
    };
  };

  const handleSubmitVerificationRequest = async (data, onDuplicateFound) => {
    try {
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes
      if (data.verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }
      const checksumFormData = new FormData();
      checksumFormData.append("file", data.verifFile[0]);

      const response = await checkDuplicateVerification.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;

      if (duplicatedSeq > 0) {
        if (onDuplicateFound) {
          onDuplicateFound(() => proceedWithVerification(data));
          return;
        }
      }

      await proceedWithVerification(data);
    } catch (error) {
      console.error("Error during verification request:", error);
    }
  };

  const handleReUploadVerification = async (data, onDuplicateFound, onSuccess, setProcessingRowId) => {
    const { verifFile } = data;

    try {
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes
      if (verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }
      const checksumFormData = new FormData();
      checksumFormData.append("file", verifFile[0]);

      const response = await checkDuplicateVerification.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;

      if (duplicatedSeq > 0) {
        if (onDuplicateFound) {
          onDuplicateFound(() => proceedWithReUpload(data, onSuccess, setProcessingRowId));
          return;
        }
      }

      await proceedWithReUpload(data, onSuccess, setProcessingRowId);
    } catch (error) {
      throw error;
    }
  };

  const appendSbomDetect = (formData, sbomDetect) => {
    if (sbomDetect?.status) {
      formData.append(
        "sbomDetect",
        new Blob([JSON.stringify(sbomDetect)], { type: "application/json" }),
      );
    }
  };

  const proceedWithReUpload = async (data, onSuccess, setProcessingRowId) => {
    const { requestComment, projectSeq, projectId, sbomSeq, sbomType, sbomDetect } = data;

    try {
      const verificationCreateForm = new FormData();
      verificationCreateForm.append("sbomFile", data.verifFile[0]);
      verificationCreateForm.append(
        "sbomType",
        new Blob([JSON.stringify(sbomType)], { type: "application/json" }),
      );
      appendSbomDetect(verificationCreateForm, sbomDetect);
      if (sbomType === SBOM_VERIFICATION_TYPE_CLARITY_SC && sbomSeq != null && sbomSeq !== "") {
        verificationCreateForm.append(
          "sbomSeq",
          new Blob([JSON.stringify(sbomSeq)], { type: "application/json" }),
        );
      }
      verificationCreateForm.append(
        "sbomVerification",
        new Blob([JSON.stringify({ projectSeq, projectId, requestComment })], { type: "application/json" }),
      );
      const response = await createSbomVerification.mutateAsync(verificationCreateForm);

      if (response.code == SUCCESS_CODE && response?.data) {
        if (setProcessingRowId) {
          setProcessingRowId(response.data?.sbomVerificationSeq);
        }

        await getVerificationData.mutateAsync(projectSeq);

        if (setProcessingRowId) {
          setProcessingRowId(null);
        }

        await getVerificationData.mutateAsync(projectSeq);

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const proceedWithVerification = async (data) => {
    const { projectSeq, projectId, requestComment, sbomSeq, sbomType, sbomDetect } = data;

    try {
      const verificationCreateForm = new FormData();
      verificationCreateForm.append("sbomFile", data.verifFile[0]);
      verificationCreateForm.append(
        "sbomType",
        new Blob([JSON.stringify(sbomType)], { type: "application/json" }),
      );
      appendSbomDetect(verificationCreateForm, sbomDetect);
      if (sbomType === SBOM_VERIFICATION_TYPE_CLARITY_SC && sbomSeq != null && sbomSeq !== "") {
        verificationCreateForm.append(
          "sbomSeq",
          new Blob([JSON.stringify(sbomSeq)], { type: "application/json" }),
        );
      }
      verificationCreateForm.append(
        "sbomVerification",
        new Blob([JSON.stringify({ projectSeq, projectId, requestComment })], { type: "application/json" }),
      );

      const response = await createSbomVerification.mutateAsync(verificationCreateForm);

      if (response.code == SUCCESS_CODE && response?.data) {
        await getVerificationData.mutateAsync(projectSeq);
      }
    } catch (error) {
      console.error("Error during verification request:", error);
    }
  };

  const createSbomVerification = useMutationData(
    queryKeys.createSbomVerification,
    {
      method: "POST",
      skipErrorToast: true,
    },
    {
      onError: (error) => {
        let errorMessage = "";
        let errorDetails = null;

        const errorCodeFromApi = error?.response?.data?.code;
        const additionalMessage = error?.response?.data?.additionalMessage;
        const apiMessages = error?.response?.data?.messages;
        const apiErrorDetails = error?.response?.data?.errorDetails;
        const apiMessageKey = error?.response?.data?.messageKey;
        const apiParams = error?.response?.data?.params;

        if (apiMessageKey) {
          errorMessage =
            formatSbomApiError(t, { messageKey: apiMessageKey, params: apiParams }, language) ||
            t(apiMessageKey, apiParams ?? {});
        } else {
          const SPECIAL_CODES = ["20100", "20101", "20102", "20103", "20106", "20107", "20108"];
          if (SPECIAL_CODES.includes(errorCodeFromApi)) {
            const errorCode = extractErrorCode(error, "common.error-occurred");
            const apiMessage = error?.response?.data?.message;
            errorMessage = apiMessage || t(errorCode);
          } else if (errorCodeFromApi === "20104") {
            errorMessage = additionalMessage
              ? `${t("errorcode.20104")} (${additionalMessage})`
              : t("errorcode.20104");
          } else if (errorCodeFromApi === "01002" && !Array.isArray(apiMessages)) {
            errorMessage = additionalMessage || t("common.error-occurred");
          } else {
            const errorCode = extractErrorCode(error, "common.error-occurred");
            errorMessage = t(errorCode);
          }
        }

        if (Array.isArray(apiErrorDetails) && apiErrorDetails.length > 0) {
          errorDetails = apiErrorDetails;
        } else if (Array.isArray(apiMessages) && apiMessages.length > 0) {
          errorDetails = apiMessages;
        } else if (additionalMessage) {
          errorDetails = [additionalMessage];
        }

        useSuccessToast(
          TOAST.error,
          t("common.error"),
          errorMessage,
          errorDetails ? () => openErrorDetailsModal(errorDetails) : null,
        );
      },
    },
  );

  const getVerificationData = useMutationData(
    (projectSeq) => `${queryKeys.sBOMVerificationByProjectSeq}?projectSeq=${projectSeq}`,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setVerificationData(normalizeResponseData(response));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const getListData = useMutationData(
    queryKeys.getSBOMList,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setListData(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / 5));
        }
      },
      onError: (error) => console.error("Error fetching SBOM list:", error),
    },
  );

  const fetchSBOMData = () => {
    const bodyRequest = { pageSize: 1000, startRow: 0 };
    getListData.mutate(bodyRequest);
  };

  useEffect(() => {
    fetchSBOMData();
  }, []);

  return {
    createSbomVerification,
    handleSubmitVerificationRequest,
    handleReUploadVerification,
    detectSbomFormat,
    isDetectPending: detectSbomFormatMutation?.isPending ?? false,
    getVerificationData,
    verificationData,
    listData,
    totalData,
    totalPages,
    getListData,
    currentPage,
    checkDuplicateVerification,
    isDuplicateCheckPending: checkDuplicateVerification?.isPending ?? false,
    isReUploadPending: createSbomVerification?.isPending ?? false,
  };
};
